/**
 * Texts picker for applying rule-based presets.
 * Select from campaign texts, add new, or generate with AI (per all / per drop).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ChevronDown, MessageSquare, Search } from 'lucide-react';

import { Button } from '@/components/ui';
import { AiGenerateDropdown } from '@/features/campaign-texts/components/AiGenerateDropdown';
import { GeneratedTextsPreview } from '@/features/campaign-texts/components/GeneratedTextsPreview';
import { GeneratedTextsPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedTextsPreviewPerDrop';
import { TextPickerItem } from '@/features/smswiser-campaigns/components/drop-modals/CreativePickerPrimitives';
import { MAX_SELECTED_TEXTS, uniqueKeepOrderStrings } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { cn } from '@/lib/utils';

import { AddNewTextBlock } from './AddNewTextBlock';
import { useApplyRuleBasedTexts } from './useApplyRuleBasedTexts';

export interface ApplyRuleBasedTextsSectionProps {
  smsTexts: string[];
  selectedTexts: string[];
  onToggleText: (text: string) => void;
  onSelectAll: () => void;
  onTextsChange: (texts: string[]) => void;
  onPerDropBatchesChange: (batches: string[][] | null) => void;
  onAiGeneratingChange?: (isGenerating: boolean) => void;
  countryKey: string;
  dropCount?: number;
  dropLabels?: Array<{ id: string; label: string }>;
  tagColors?: Record<string, string>;
  isBusy: boolean;
}

export const ApplyRuleBasedTextsSection: FC<ApplyRuleBasedTextsSectionProps> = ({
  smsTexts,
  selectedTexts,
  onToggleText,
  onSelectAll: _onSelectAll,
  onTextsChange,
  onPerDropBatchesChange,
  onAiGeneratingChange,
  countryKey,
  dropCount,
  dropLabels,
  tagColors,
  isBusy,
}) => {
  const {
    search,
    setSearch,
    showAddNew,
    setShowAddNew,
    newTextValue,
    setNewTextValue,
    newTextError,
    setNewTextError,
    filteredTexts,
    addTexts,
    applyNewTextInput,
    ai,
  } = useApplyRuleBasedTexts({
    countryKey,
    campaignTexts: smsTexts,
    selectedTexts,
    onTextsChange,
    onPerDropBatchesChange,
    dropCount,
    isBusy,
  });

  const handleSelectAllFiltered = useCallback(() => {
    const cap = Math.min(filteredTexts.length, MAX_SELECTED_TEXTS);
    const isDeselect = selectedTexts.length >= cap && selectedTexts.length > 0;
    if (isDeselect) {
      onTextsChange(selectedTexts.filter((t) => !filteredTexts.includes(t)));
    } else {
      onTextsChange(uniqueKeepOrderStrings([...selectedTexts, ...filteredTexts]).slice(0, MAX_SELECTED_TEXTS));
    }
  }, [filteredTexts, selectedTexts, onTextsChange]);

  const selectAllLabel =
    selectedTexts.length >= Math.min(filteredTexts.length, MAX_SELECTED_TEXTS) && selectedTexts.length > 0
      ? 'Deselect all'
      : 'Select all';

  const hasAiGenerated = Boolean(
    (ai.perDropBatches?.length ?? 0) > 0 || ai.generatedTexts.length > 0,
  );

  return (
    <section className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5" />
          Texts
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs',
              selectedTexts.length > MAX_SELECTED_TEXTS
                ? 'bg-destructive/20 text-destructive font-semibold'
                : 'bg-primary/20 text-primary',
            )}
          >
            {selectedTexts.length}/{MAX_SELECTED_TEXTS}
          </span>
        </h4>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={handleSelectAllFiltered}
            disabled={isBusy || filteredTexts.length === 0}
          >
            {selectAllLabel}
          </Button>
          {(dropCount ?? 0) > 0 ? (
            <AiGenerateDropdown
              countryKey={countryKey}
              dropCount={dropCount ?? 0}
              dropLabels={dropLabels}
              onGeneratingChange={onAiGeneratingChange}
              onGenerated={ai.handleAiGenerated}
            />
          ) : (
            <span className="text-xs text-muted-foreground">
              {dropCount === undefined ? 'Checking…' : 'No matching lists'}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 gap-1 text-xs"
            onClick={() => setShowAddNew((v) => !v)}
          >
            <ChevronDown className={cn('h-3 w-3 transition-transform', showAddNew && 'rotate-180')} />
            New text
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none placeholder:text-muted-foreground"
          placeholder="Filter texts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {showAddNew && (
        <AddNewTextBlock
          value={newTextValue}
          onChange={(v) => {
            setNewTextValue(v);
            setNewTextError(null);
          }}
          error={newTextError}
          onAdd={applyNewTextInput}
          onPasteAdd={addTexts}
          disabled={isBusy}
        />
      )}

      <AnimatePresence>
        {ai.perDropBatches && ai.perDropBatches.length > 0 ? (
          <GeneratedTextsPreviewPerDrop
            batches={ai.perDropBatches}
            translationsPerDrop={ai.perDropTranslations}
            groupIdsPerDrop={ai.groupIdsPerDrop}
            onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
            isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
            dropLabels={dropLabels ?? Array.from({ length: dropCount ?? 0 }, (_, i) => ({ id: String(i), label: `List ${i + 1}` }))}
            onSave={ai.handleSavePerDrop}
            onDismiss={ai.handleDismissPerDrop}
            onRegenerate={ai.handleRegeneratePerDrop}
            onRegenerateAll={ai.handleRegenerateAllPerDrop}
            onRegenerateOverLength={ai.handleRegenerateOverLengthPerDrop}
            onRegenerateFailed={ai.handleRegenerateFailedPerDrop}
            onSaveAndHide={ai.handleSaveAndHidePerDrop}
            onTranslatePerDropAll={ai.handleTranslatePerDropAll}
            onTranslatePerDropMissing={ai.handleTranslatePerDropMissing}
            onTranslateAllPerDrop={ai.handleTranslateAllPerDrop}
            isRegenerating={ai.isRegenerating}
            isTranslating={ai.isTranslating}
            useAndHideOnly
            applied={ai.perDropApplied}
          />
        ) : ai.generatedTexts.length > 0 ? (
          <GeneratedTextsPreview
            texts={ai.generatedTexts}
            translations={ai.generatedTranslations}
            onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
            isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
            onSave={ai.handleSaveGenerated}
            onDismiss={ai.handleDismiss}
            onRegenerate={ai.handleRegenerate}
            onRegenerateOverLength={ai.handleRegenerateOverLength}
            onTranslateAll={ai.handleTranslateAll}
            onTranslateMissing={ai.handleTranslateMissing}
            isRegenerating={ai.isRegenerating}
            isTranslating={ai.isTranslating}
          />
        ) : null}
      </AnimatePresence>

      {!hasAiGenerated && (
        <div className="max-h-[min(320px,50vh)] min-h-[100px] space-y-1 overflow-y-auto rounded-lg border border-border bg-muted/10 p-2">
          {filteredTexts.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              No texts. Add new or use AI to generate.
            </p>
          ) : (
            filteredTexts.map((text) => (
              <TextPickerItem
                key={text}
                text={text}
                isSelected={selectedTexts.includes(text)}
                onToggle={() => onToggleText(text)}
                tagColors={tagColors}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
};
